import { useCallback, useContext, useState } from 'react'

import { TEMP_COMMENT_ID, TEMP_REPLY_ID } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { Comment } from '@editor/interfaces/interfaces'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'

import { fetchCommentReplies, setEditingComment, setEditingReply, setFormImages } from '../store/comments'

export interface CommentPopupProps {
  /**
   * Comment to be shown in the popup.
   */
  comment: Comment

  /**
   * Triggered when user closes the popup.
   */
  onClose: () => void

  /**
   * Triggered when user initiates moving of comment.
   */
  onMove?: () => void
}

export const useCommentPopup = ({ comment, onClose }: CommentPopupProps) => {
  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const editingComment = useSelector((state: RootState) => state.comments.editingComment)
  const editingReply = useSelector((state: RootState) => state.comments.editingReply)
  const user = useSelector((state: RootState) => state.user.user)

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)

  // temp vars
  const isDisabled = false

  // Vars
  const isNewComment = comment.thread_id === TEMP_COMMENT_ID

  // State
  const [isLoading, setIsLoading] = useState(false)

  /**
   * Reset the form.
   */
  const handleResetForm = useCallback(() => {
    dispatch(setEditingReply(null))
    dispatch(setEditingComment(null))
    dispatch(setFormImages([]))
  }, [dispatch])

  /**
   * Cancel input of new comment/reply.
   */
  const onCancelInput = () => {
    onClose()

    // remove temp comment from store
    if (isNewComment) {
      handleResetForm()
    }
  }

  /**
   * Callback after saving of comment/reply.
   */
  const onSaved = () => {
    handleResetForm()

    if (comment.thread_id !== TEMP_COMMENT_ID) {
      void fetchReplies()
    }
  }

  /**
   * Set a dummy reply to show the reply form.
   */
  const showReplyForm = () => {
    if (!comment.thread_id || !user) return
    dispatch(setFormImages([]))
    dispatch(setEditingComment(null))
    dispatch(
      setEditingReply({
        reply_id: TEMP_REPLY_ID,
        author_name: user.nickname || '',
        reply_body: '',
        images: [],
        inspection_area_id: comment.inspection_area_id,
      }),
    )
  }

  /**
   * Fetch replies for a comment.
   */
  const fetchReplies = async () => {
    if (!project || !comment.inspection_area_id || !comment?.thread_id) {
      return false
    }

    setIsLoading(true)

    const token = await getAccessToken()
    if (!token) {
      setIsLoading(false)
      return false
    }

    await dispatch(
      fetchCommentReplies({
        token,
        project_id: project.project_id,
        inspection_area_id: comment.inspection_area_id,
        thread_id: comment.thread_id,
        showErrorModal,
      }),
    )

    setIsLoading(false)
    return true
  }

  return {
    // Flags
    isDisabled,
    isLoading,
    isNewComment,
    isInputShown: editingComment?.thread_id === TEMP_COMMENT_ID || editingReply?.reply_id === TEMP_REPLY_ID,

    // States
    setIsLoading,

    // event handlers
    onCancelInput,
    onSaved,

    // functions
    handleClose: onClose,
    showReplyForm,
    fetchReplies,
  }
}
